import React, { useEffect, useState } from "react";
import jsPDF from 'jspdf';
import { formatDateLong, formatDateInputValue } from "../utils/format";

export default function LeaseAgreementModal({
  open,
  onClose,
  selectedUnit,
  properties,
  managementCompanies,
  selectedManagementCompany,
  user: currentUser,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filledText, setFilledText] = useState("");

  const LEASE_TEMPLATE_URL = "/documents/FRP_Lease_Agreement.docx";

  const loadExternalScript = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });

  const ensureDeps = async () => {
    await loadExternalScript('https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js');
  };

  const getId = (val) => {
    const v = (val && (val._id || val.id || val)) || null;
    return v ? String(v) : null;
  };

  const getLeaseTemplateData = () => {
    const agreementDate = formatDateLong(new Date());
    let managementCompanyName = "";

    if (selectedManagementCompany) {
      managementCompanyName = selectedManagementCompany.companyName
        || selectedManagementCompany.name
        || selectedManagementCompany.company
        || "";
    }

    if (!managementCompanyName) {
      const pmc = selectedUnit?.property?.managementCompany;
      if (pmc) {
        if (typeof pmc === 'object') {
          managementCompanyName = pmc.companyName || pmc.name || pmc.company || "";
        } else {
          const match = (managementCompanies || []).find(mc => getId(mc) === getId(pmc));
          if (match) managementCompanyName = match.companyName || match.name || match.company || "";
        }
      }
    }

    if (!managementCompanyName) {
      const propId = getId(selectedUnit?.property?._id || selectedUnit?.property);
      const fullProp = (properties || []).find(p => getId(p) === propId);
      if (fullProp) {
        const pmc = fullProp.managementCompany;
        if (pmc) {
          if (typeof pmc === 'object') {
            managementCompanyName = pmc.companyName || pmc.name || pmc.company || "";
          } else {
            const match = (managementCompanies || []).find(mc => getId(mc) === getId(pmc));
            if (match) managementCompanyName = match.companyName || match.name || match.company || "";
          }
        }
      }
    }

    // If the current user is a tenant (current role) OR appears in tenantHistory for this unit,
    // prefer the logged-in user's identity as the tenant for the agreement.
    const isCurrentUserTenant = !!(currentUser && (
      currentUser.role === 'T' || (Array.isArray(currentUser.propertyRoles) && currentUser.propertyRoles.some(pr => pr.role === 'T'))
    ));
    const currentUserId = currentUser?._id || currentUser?.id || null;
    const appearsInHistory = Array.isArray(selectedUnit?.tenantHistory)
      ? selectedUnit.tenantHistory.some(th => getId(th?.tenant) && getId(th?.tenant) === getId(currentUserId))
      : false;
    const tenantName = (isCurrentUserTenant || appearsInHistory)
      ? (currentUser?.name || currentUser?.email || "")
      : (
        (selectedUnit?.tenant?.user?.name)
        || (selectedUnit?.tenant?.name)
        || (Array.isArray(selectedUnit?.tenants) && selectedUnit.tenants.length > 0
          ? selectedUnit.tenants.map(t => t?.user?.name || t?.user?.email || "Unknown").join(", ")
          : "")
      );
    const propertyName = selectedUnit?.property?.name || "";
    const unitType = selectedUnit?.type || "";
    const unitNumber = selectedUnit?.label || selectedUnit?.unitNumber || "";

    // Use centralized Nairobi-time formatter; fall back to placeholder if invalid
    const formatISO = (d) => {
      const result = formatDateInputValue(d);
      return result || '0000-00-00';
    };

    // Determine the tenant to reference for dates/rent
    const targetTenantId = (isCurrentUserTenant || appearsInHistory)
      ? (currentUser?._id || currentUser?.id || null)
      : (
        (selectedUnit?.tenant?._id || selectedUnit?.tenant) ||
        (Array.isArray(selectedUnit?.tenants) && selectedUnit.tenants.length > 0 ? (selectedUnit.tenants[0]?.user?._id || selectedUnit.tenants[0]?.user) : null) ||
        (Array.isArray(selectedUnit?.tenantHistory) && selectedUnit.tenantHistory.find(th => !th.endDate)?.tenant?._id)
      );

    const historyForTarget = Array.isArray(selectedUnit?.tenantHistory)
      ? selectedUnit.tenantHistory
          .filter(th => {
            const id = th?.tenant?._id || th?.tenant;
            return targetTenantId && id && String(id) === String(targetTenantId);
          })
          .sort((a, b) => new Date(a.startDate || 0) - new Date(b.startDate || 0))
      : [];

    // Prefer active period, else most recent
    const activePeriod = historyForTarget.find(th => !th.endDate) || historyForTarget[historyForTarget.length - 1] || null;

    const moveInDate = formatISO(activePeriod?.startDate);
    const moveOutDate = activePeriod && activePeriod.endDate ? formatISO(activePeriod.endDate) : '0000-00-00';

    let monthlyRent = '';
    if (activePeriod && activePeriod.endDate) {
      // Former tenancy -> use rentAtStart if available
      monthlyRent = (typeof activePeriod.rentAtStart === 'number') ? String(activePeriod.rentAtStart) : String(selectedUnit?.rentAmount || '');
    } else {
      // Current tenancy or generic -> use current unit rent
      monthlyRent = String(selectedUnit?.rentAmount || '');
    }

    return {
      agreement_date: agreementDate,
      management_company_name: managementCompanyName,
      tenant_name: tenantName,
      property_name: propertyName,
      unit_type: unitType,
      unit_number: unitNumber,
      move_in_date: moveInDate,
      move_out_date: moveOutDate,
      monthly_rent: monthlyRent,
    };
  };

  const extractTextFromDocxXml = (xmlString) => {
    try {
      const paragraphRegex = /<w:p[\s\S]*?>[\s\S]*?<\/w:p>/g;
      const textRegex = /<w:t[^>]*>([\s\S]*?)<\/w:t>/g;
      const paragraphs = [];
      const pMatches = xmlString.match(paragraphRegex) || [];
      const decoder = document.createElement('textarea');
      pMatches.forEach(p => {
        let texts = [];
        let m;
        while ((m = textRegex.exec(p)) !== null) {
          decoder.innerHTML = m[1];
          texts.push(decoder.value);
        }
        let line = texts.join('');
        if (/<w:numPr[\s\S]*?>/.test(p)) {
          line = (line?.trim()?.length ? `• ${line}` : '•');
        }
        paragraphs.push(line);
      });
      return paragraphs.join('\n');
    } catch (_) {
      return '';
    }
  };

  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const fillPlaceholdersInText = (text, data) => {
    if (!text) return '';
    return text
      .replaceAll('{agreement_date}', data.agreement_date || '')
      .replaceAll('{management_company_name}', data.management_company_name || '')
      .replaceAll('{tenant_name}', data.tenant_name || '')
      .replaceAll('{property_name}', data.property_name || '')
      .replaceAll('{unit_type}', data.unit_type || '')
      .replaceAll('{unit_number}', data.unit_number || '')
      .replaceAll('{move_in_date}', data.move_in_date || '0000-00-00')
      .replaceAll('{move_out_date}', data.move_out_date || '0000-00-00')
      .replaceAll('{monthly_rent}', data.monthly_rent || '');
  };

  const normalizeLeaseText = (text) => {
    if (!text) return '';
    let cleaned = text;
    cleaned = cleaned.replace(/<[^>]+>/g, '');
    cleaned = cleaned.replace(/\r/g, '').replace(/\u00A0/g, ' ');
    cleaned = cleaned.replace(/[\t ]+/g, ' ');
    cleaned = cleaned.replace(/ \n/g, '\n');
    const labels = [
      'Agreement Date:',
      'Management:',
      'Tenant:',
      'Property Name:',
      'Unit Type:',
      'Unit Number:'
    ];
    labels.forEach((label) => {
      const re = new RegExp(`(?<!^|\\n)\\s*${escapeRegex(label)}`, 'g');
      try {
        cleaned = cleaned.replace(re, `\n${label}`);
      } catch (_) {
        const re2 = new RegExp(`\\s*${escapeRegex(label)}`, 'g');
        cleaned = cleaned.replace(re2, `\n${label}`);
      }
    });
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    cleaned = cleaned.split('\n').map(l => l.replace(/\s+$/,'')).join('\n');
    return cleaned.trim();
  };

  const loadLeaseText = async () => {
    if (!open) return;
    try {
      setLoading(true);
      setError("");
      setFilledText("");
      await ensureDeps();
      const resp = await fetch(LEASE_TEMPLATE_URL, { cache: 'no-store' });
      if (!resp.ok) throw new Error(`Template not found (${resp.status})`);
      const arrayBuffer = await resp.arrayBuffer();
      const zip = await window.JSZip.loadAsync(arrayBuffer);
      const documentFile = zip.file('word/document.xml');
      if (!documentFile) throw new Error('Invalid DOCX file');
      const documentXml = await documentFile.async('string');
      const plainText = extractTextFromDocxXml(documentXml);
      const data = getLeaseTemplateData();
      const filled = normalizeLeaseText(fillPlaceholdersInText(plainText, data));
      setFilledText(filled || '');
    } catch (e) {
      console.error('Lease text load error:', e);
      setError('Rendered simplified text version.');
      const data = getLeaseTemplateData();
      const fallback = [
        `Agreement Date: {agreement_date}`,
        `Management: {management_company_name}`,
        `Tenant: {tenant_name}`,
        `Property Name: {property_name}`,
        `Unit Type: {unit_type}`,
        `Unit Number: {unit_number}`,
      ].join('\n');
      setFilledText(normalizeLeaseText(fillPlaceholdersInText(fallback, data)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadLeaseText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUnit, selectedManagementCompany]);

  const downloadPdf = async () => {
    try {
      setLoading(true);
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      const usableWidth = pageWidth - margin * 2;
      doc.setFontSize(16);
      doc.text('LEASE AGREEMENT', margin, 60);
      doc.setFontSize(11);
      const text = filledText || '';
      const lines = doc.splitTextToSize(text, usableWidth);
      let y = 90;
      const lineHeight = 14;
      lines.forEach((line) => {
        if (y > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line || ' ', margin, y);
        y += lineHeight;
      });
      const todayStr = formatDateInputValue(new Date());
      doc.save(`Lease_Agreement_${todayStr}.pdf`);
    } catch (e) {
      console.error('PDF download error:', e);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">Lease Agreement</h2>
            <div className="flex gap-2">
              <button
                onClick={downloadPdf}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Preparing…' : 'Download PDF'}
              </button>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl px-2"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="w-full bg-gray-50 border border-gray-300 rounded p-4" style={{ maxHeight: '75vh' }}>
            {loading ? (
              <div className="flex items-center justify-center h-[60vh] text-gray-500">Loading document…</div>
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-gray-800">{filledText}</pre>
            )}
            {error && (
              <div className="mt-2 text-xs text-red-600">{error}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
